/**
 * Font Loader Utility for Omnivaso Platform
 * Supports Google Fonts, External Web Stylesheets (Adobe Fonts, Fontshare, etc.), 
 * and Uploaded/Direct Font Files (.woff2, .woff, .ttf, .otf) with @font-face rules.
 */

// Helper to determine format string from font file URL
export const getFontFormatFromUrl = (url = '') => {
    const cleanUrl = url.split('?')[0].toLowerCase();
    if (cleanUrl.endsWith('.woff2')) return 'woff2';
    if (cleanUrl.endsWith('.woff')) return 'woff';
    if (cleanUrl.endsWith('.ttf')) return 'truetype';
    if (cleanUrl.endsWith('.otf')) return 'opentype';
    return 'woff2';
};

// Generate CSS font-family property value for a custom font
export const getCustomFontCssFamily = (font) => {
    if (!font) return 'sans-serif';
    if (font.value && font.value.includes(',')) return font.value;
    
    const name = (font.name || font.label || '').trim().replace(/['"]/g, '');
    if (!name) return 'sans-serif';

    const category = (font.category || 'Sans-Serif').toLowerCase();
    let fallback = 'sans-serif';
    if (category.includes('serif')) fallback = 'serif';
    else if (category.includes('mono')) fallback = 'monospace';
    else if (category.includes('script') || category.includes('hand')) fallback = 'cursive';
    else if (category.includes('display')) fallback = 'sans-serif';

    return `'${name}', ${fallback}`;
};

// Build Google Fonts CSS URL for a given font name
export const buildGoogleFontUrl = (fontName, weights = 'wght@300;400;500;600;700;800;900') => {
    const cleanName = (fontName || '').trim().replace(/['"]/g, '');
    if (!cleanName) return '';
    const encodedName = cleanName.split(' ').map(part => encodeURIComponent(part)).join('+');
    return `https://fonts.googleapis.com/css2?family=${encodedName}:${weights}&display=swap`;
};

/**
 * Injects or updates custom font stylesheets and @font-face rules in document.head
 * @param {Array} customFonts - Array of custom font definitions
 */
export const loadCustomFonts = (customFonts = []) => {
    if (typeof document === 'undefined') return;

    if (!Array.isArray(customFonts)) {
        return;
    }

    const activeIds = new Set();

    customFonts.forEach((font, index) => {
        if (!font) return;
        const id = font.id || `cf-${index}-${(font.name || '').replace(/[^a-zA-Z0-9]/g, '_')}`;
        const fontName = (font.name || font.label || '').trim().replace(/['"]/g, '');
        if (!fontName) return;

        activeIds.add(id);

        const sourceType = font.sourceType || (font.fileUrl ? 'file' : (font.url && font.url.startsWith('http') && !font.url.includes('google') ? 'url' : 'google'));

        if (sourceType === 'google') {
            const fontUrl = font.url && font.url.startsWith('http') ? font.url : buildGoogleFontUrl(fontName);
            let linkEl = document.querySelector(`link[data-custom-font="${id}"]`);
            if (!linkEl) {
                linkEl = document.createElement('link');
                linkEl.rel = 'stylesheet';
                linkEl.setAttribute('data-custom-font', id);
                document.head.appendChild(linkEl);
            }
            if (linkEl.href !== fontUrl) {
                linkEl.href = fontUrl;
            }
        } else if (sourceType === 'url') {
            const stylesheetUrl = font.url || '';
            if (stylesheetUrl) {
                let linkEl = document.querySelector(`link[data-custom-font="${id}"]`);
                if (!linkEl) {
                    linkEl = document.createElement('link');
                    linkEl.rel = 'stylesheet';
                    linkEl.setAttribute('data-custom-font', id);
                    document.head.appendChild(linkEl);
                }
                if (linkEl.href !== stylesheetUrl) {
                    linkEl.href = stylesheetUrl;
                }
            }
        } else if (sourceType === 'file') {
            const fileUrl = font.fileUrl || font.url || '';
            if (fileUrl) {
                const format = font.format || getFontFormatFromUrl(fileUrl);
                const weight = font.weight || font.fontWeight || 'normal';
                const style = font.style || font.fontStyle || 'normal';
                const fontFaceRule = `
@font-face {
    font-family: '${fontName}';
    src: url('${fileUrl}') format('${format}');
    font-weight: ${weight};
    font-style: ${style};
    font-display: swap;
}
`;
                let styleEl = document.querySelector(`style[data-custom-font="${id}"]`);
                if (!styleEl) {
                    styleEl = document.createElement('style');
                    styleEl.setAttribute('data-custom-font', id);
                    document.head.appendChild(styleEl);
                }
                if (styleEl.textContent !== fontFaceRule) {
                    styleEl.textContent = fontFaceRule;
                }
            }
        }
    });

    // Cleanup removed fonts from DOM
    const existingFontEls = document.querySelectorAll('[data-custom-font]');
    existingFontEls.forEach(el => {
        const elId = el.getAttribute('data-custom-font');
        if (elId && !activeIds.has(elId)) {
            el.remove();
        }
    });
};
